import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2 group">
          <span
            className="font-serif-display text-2xl md:text-3xl tracking-tight font-light text-white"
            style={{ letterSpacing: "-0.03em" }}
          >
            Elite<span style={{ color: "#C2533B" }}>.</span>
          </span>
          <span className="hidden md:inline overline text-zinc-500" style={{ color: "#71717A" }}>
            Solutions Hub
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`navbar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <Link to="/contact" data-testid="navbar-contact-cta" className="btn-primary">
            Start a Project <ArrowUpRight size={14} strokeWidth={1.8} />
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-panel"
          className="lg:hidden border-t border-white/5 bg-[#050505]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-serif-display text-3xl py-3 transition-colors ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-contact-cta" className="btn-primary mt-6 w-full">
              Start a Project <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
